<script setup>
import { computed, onMounted, ref } from 'vue';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import { authorizeTiendaNube } from '../../../api/tiendaNube';

defineProps({
  error: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();
const integrationLoaded = ref(false);
const isSubmitting = ref(false);
const viewMode = ref('overview');
const editingHookId = ref(null);
const integration = useFunctionGetter(
  'integrations/getIntegration',
  'tienda_nube'
);
const integrationId = computed(() => integration.value?.id || 'tienda_nube');
const integrationName = computed(() => integration.value?.name || 'Nuvemshop');
const connectedHooks = computed(() => {
  return (integration.value?.hooks || []).map(hook => ({
    id: hook.id,
    storeDomain: hook.settings?.store_domain || hook.reference_id || '',
    webhookToken: hook.settings?.webhook_token || '',
    scope: hook.settings?.scope || '',
  }));
});
const integrationDescription = computed(
  () =>
    integration.value?.description ||
    t('INTEGRATION_SETTINGS.TIENDA_NUBE.DESCRIPTION')
);
const selectedHook = computed(() => {
  return (
    connectedHooks.value.find(hook => hook.id === editingHookId.value) || null
  );
});
const accountId = computed(() => store.getters.getCurrentAccountId);
const pluginConfig = computed(
  () => window.chatwoot?.plugins?.['tienda-nube']?.config || {}
);
const automationAuthHeader = computed(
  () => pluginConfig.value.AUTOMATION_AUTH_HEADER
);
const automationEventKeys = computed(
  () => pluginConfig.value.AUTOMATION_EVENT_KEYS || []
);
const automationEventLabels = computed(
  () => pluginConfig.value.AUTOMATION_EVENT_LABELS || {}
);
const payloadFields = computed(
  () => pluginConfig.value.AUTOMATION_PAYLOAD_FIELDS || []
);
const automationPayloadExample = computed(() =>
  JSON.stringify(
    {
      event_name: 'tienda_nube_order_created',
      customer: {
        email: 'jane.doe@example.com',
        phone_number: '+55 11 99999-0000',
        name: 'Jane Doe',
        external_id: 'customer_42',
      },
      order: {
        id: '5001',
        number: '#5001',
        status: 'open',
        payment_status: 'paid',
        total: '189.90',
        currency: 'BRL',
      },
      raw_payload: {
        provider: 'nuvemshop-app',
      },
    },
    null,
    2
  )
);
const abandonedCheckoutPayloadExample = computed(() =>
  JSON.stringify(
    {
      event_name: 'tienda_nube_abandoned_checkout_detected',
      customer: {
        email: 'john.smith@example.com',
        phone_number: '+55 21 98888-7777',
        name: 'John Smith',
        external_id: 'customer_99',
      },
      order: {
        id: 'checkout-9001',
        number: 'checkout-9001',
        status: 'abandoned',
        payment_status: 'pending',
        total: '79.00',
        currency: 'BRL',
      },
      raw_payload: {
        checkout_url: 'https://store.example.com/checkout/9001',
        abandoned_checkout_url: 'https://store.example.com/abandoned/9001',
      },
    },
    null,
    2
  )
);
const automationWebhookUrl = computed(() => {
  return `${window.location.origin}/tienda_nube/accounts/${accountId.value}/events`;
});

const buildAutomationAuthHeaderExample = token => {
  return `${automationAuthHeader.value}: Bearer ${token || '...'}`;
};

const openCreateView = () => {
  editingHookId.value = null;
  viewMode.value = 'details';
};

const openEditView = hook => {
  editingHookId.value = hook.id;
  viewMode.value = 'details';
};

const openOverview = () => {
  editingHookId.value = null;
  viewMode.value = 'overview';
};

const handleCopy = async value => {
  if (!value) {
    return;
  }

  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};

const handleConnect = async () => {
  try {
    isSubmitting.value = true;
    const currentAccountId = store.getters.getCurrentAccountId;
    const { data } = await authorizeTiendaNube(currentAccountId);

    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    }
  } catch {
    // Redirect on success, the page error state handles callback failures.
  } finally {
    isSubmitting.value = false;
  }
};

const handleDeleteHook = async hookId => {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: integrationId.value,
      hookId,
    });
    await store.dispatch('integrations/get');
    if (editingHookId.value === hookId) {
      openOverview();
    }
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE'));
  }
};

const initializeIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeIntegration();
});
</script>

<template>
  <div class="flex flex-col flex-1 gap-6 overflow-auto pt-1 pb-10">
    <template v-if="integrationLoaded">
      <section
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div
          class="flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between"
        >
          <div class="max-w-3xl">
            <h2 class="mb-2 text-xl font-medium text-n-slate-12">
              {{ integrationName }}
            </h2>
            <p class="m-0 text-sm leading-6 text-n-slate-11">
              {{ integrationDescription }}
            </p>
          </div>
          <div class="flex gap-3">
            <Button
              v-if="viewMode === 'details'"
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="openOverview"
            />
            <Button
              teal
              :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
              @click="openCreateView"
            />
          </div>
        </div>
      </section>

      <section
        v-if="viewMode === 'overview' && connectedHooks.length"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="grid grid-cols-1 gap-4 p-6 xl:grid-cols-2">
          <div
            v-for="hook in connectedHooks"
            :key="hook.id"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 break-all text-sm font-medium text-n-slate-12">
                  {{ hook.storeDomain }}
                </p>
                <p
                  v-if="hook.scope"
                  class="mb-0 break-all text-xs text-n-slate-10"
                >
                  {{ hook.scope }}
                </p>
              </div>
              <div class="flex gap-2">
                <Button
                  slate
                  xs
                  faded
                  :label="$t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')"
                  @click="openEditView(hook)"
                />
                <Button
                  ruby
                  xs
                  faded
                  :label="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')"
                  @click="handleDeleteHook(hook.id)"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="viewMode === 'overview' && !connectedHooks.length"
        class="rounded-xl bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
      >
        <p class="m-0 text-sm leading-6 text-n-slate-11">
          {{ $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', { integrationId }) }}
        </p>
      </section>

      <section
        v-if="viewMode === 'details'"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="border-b border-n-weak p-6">
          <h3 class="text-xl font-medium text-n-slate-12">
            {{
              selectedHook
                ? $t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')
                : $t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')
            }}
          </h3>
          <p class="mb-0 mt-2 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.DESCRIPTION') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 p-6 lg:grid-cols-2">
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.WEBHOOK_URL') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{
                $t('INTEGRATION_SETTINGS.TIENDA_NUBE.STORE_DOMAIN_HINT', {
                  storeDomain: selectedHook?.storeDomain || '—',
                })
              }}
            </p>
            <code
              class="mt-4 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
            >
              {{ automationWebhookUrl }}
            </code>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.AUTH_HEADER') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{
                selectedHook?.scope ||
                $t('INTEGRATION_SETTINGS.TIENDA_NUBE.ABANDONED_HINT')
              }}
            </p>
            <code
              class="mt-4 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
            >
              {{
                buildAutomationAuthHeaderExample(
                  selectedHook?.webhookToken || '...'
                )
              }}
            </code>
          </div>
        </div>

        <div class="border-t border-n-weak p-6">
          <div class="flex gap-3">
            <Button
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="openOverview"
            />
            <Button
              teal
              :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              :is-loading="isSubmitting"
              @click="handleConnect"
            />
          </div>
        </div>
      </section>

      <div
        v-if="error"
        class="flex flex-1 items-center justify-center rounded-md bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
      >
        <p class="text-n-ruby-9">
          {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.ERROR') }}
        </p>
      </div>

      <section
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="border-b border-n-weak p-6">
          <h3 class="text-xl font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.AUTOMATION_TITLE') }}
          </h3>
          <p class="mb-0 mt-2 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.AUTOMATION_DESCRIPTION') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 p-6 lg:grid-cols-2">
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.WEBHOOK_URL') }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{
                    $t('INTEGRATION_SETTINGS.TIENDA_NUBE.STORE_DOMAIN_HINT', {
                      storeDomain:
                        selectedHook?.storeDomain ||
                        connectedHooks[0]?.storeDomain ||
                        '—',
                    })
                  }}
                </p>
              </div>
              <Button
                slate
                xs
                faded
                :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                @click="handleCopy(automationWebhookUrl)"
              />
            </div>
            <code
              class="mt-4 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
            >
              {{ automationWebhookUrl }}
            </code>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.AUTH_HEADER') }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.ABANDONED_HINT') }}
                </p>
              </div>
              <Button
                slate
                xs
                faded
                :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                @click="
                  handleCopy(
                    buildAutomationAuthHeaderExample(
                      selectedHook?.webhookToken || '...'
                    )
                  )
                "
              />
            </div>
            <code
              class="mt-4 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
            >
              {{
                buildAutomationAuthHeaderExample(
                  selectedHook?.webhookToken || '...'
                )
              }}
            </code>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 px-6 pb-6 lg:grid-cols-2">
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <p class="mb-3 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.EXPECTED_EVENTS') }}
            </p>
            <ul class="mb-0 space-y-2 pl-4 text-sm text-n-slate-11">
              <li v-for="eventKey in automationEventKeys" :key="eventKey">
                <code>{{ eventKey }}</code>
                <span class="ml-2">{{
                  automationEventLabels[eventKey] || eventKey
                }}</span>
              </li>
            </ul>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <p class="mb-3 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TIENDA_NUBE.EXPECTED_PAYLOAD') }}
            </p>
            <ul class="mb-0 space-y-2 pl-4 text-sm text-n-slate-11">
              <li v-for="field in payloadFields" :key="field">
                <code>{{ field }}</code>
              </li>
            </ul>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 px-6 pb-6 xl:grid-cols-2">
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{
                    $t('INTEGRATION_SETTINGS.TIENDA_NUBE.PAYLOAD_EXAMPLE_TITLE')
                  }}
                </p>
                <p class="mb-0 text-xs leading-5 text-n-slate-10">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.TIENDA_NUBE.PAYLOAD_EXAMPLE_DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <Button
                slate
                xs
                faded
                :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                @click="handleCopy(automationPayloadExample)"
              />
            </div>
            <pre
              class="mt-4 overflow-x-auto rounded-md bg-n-alpha-2 px-3 py-3 text-xs leading-6 text-n-slate-11"
            ><code>{{ automationPayloadExample }}</code></pre>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.TIENDA_NUBE.ABANDONED_PAYLOAD_TITLE'
                    )
                  }}
                </p>
                <p class="mb-0 text-xs leading-5 text-n-slate-10">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.TIENDA_NUBE.ABANDONED_PAYLOAD_DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <Button
                slate
                xs
                faded
                :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                @click="handleCopy(abandonedCheckoutPayloadExample)"
              />
            </div>
            <pre
              class="mt-4 overflow-x-auto rounded-md bg-n-alpha-2 px-3 py-3 text-xs leading-6 text-n-slate-11"
            ><code>{{ abandonedCheckoutPayloadExample }}</code></pre>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
